/**
 * Map annotations — pin (point + label) and text (label only). All
 * annotations are tracked in `annotations` so we can clear them all and
 * also include them in PNG exports (see io/export.js).
 */
import maplibregl from 'maplibre-gl';
import { APP } from '../state.js';
import { toast } from '../ui/toast.js';

let clickHandler = null;
let mode = null;            // 'pin' | 'text'

/**
 * Live registry of every annotation on the map. Each entry holds the
 * MapLibre Marker plus enough metadata for the clearer + the PNG export
 * to know what's been placed.
 */
export const annotations = [];

// ---------------------------------------------------------------------------
// Pin (existing behaviour)
// ---------------------------------------------------------------------------

export function activateDraw(map) {
  return _activate(map, 'pin', 'tool-draw',
    'Click on the map to place a pin');
}

// ---------------------------------------------------------------------------
// Text-only annotation (no pin, just typography on the map)
// ---------------------------------------------------------------------------

export function activateText(map) {
  return _activate(map, 'text', 'tool-text',
    'Click on the map to place a text label');
}

// ---------------------------------------------------------------------------
// Clear every annotation
// ---------------------------------------------------------------------------

export function clearAnnotations(map) {
  if (annotations.length === 0) {
    toast('No annotations to clear');
    return;
  }
  for (const a of annotations) a.marker.remove();
  annotations.length = 0;
  toast('Annotations cleared');
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function _activate(map, newMode, btnId, helpText) {
  // Clicking the same tool twice toggles it off
  if (APP.activeTool === newMode) {
    _deactivate(map, btnId);
    return;
  }
  // If a different annotation tool was active, swap to the new one
  if (APP.activeTool === 'pin' || APP.activeTool === 'text') {
    document.getElementById(`tool-${APP.activeTool === 'pin' ? 'draw' : 'text'}`)?.classList.remove('active');
    if (clickHandler) { map.off('click', clickHandler); clickHandler = null; }
  }

  APP.activeTool = newMode;
  mode = newMode;
  map.getCanvas().style.cursor = 'crosshair';
  document.getElementById(btnId)?.classList.add('active');
  toast(helpText);

  clickHandler = e => onClick(map, e);
  map.on('click', clickHandler);
}

function _deactivate(map, btnId) {
  APP.activeTool = null;
  mode = null;
  map.getCanvas().style.cursor = '';
  document.getElementById(btnId)?.classList.remove('active');
  if (clickHandler) {
    map.off('click', clickHandler);
    clickHandler = null;
  }
}

function onClick(map, e) {
  if (APP.activeTool !== mode) return;

  const text = prompt(mode === 'pin' ? 'Annotation label:' : 'Text annotation:');
  if (!text) return;

  if (mode === 'pin') {
    const marker = new maplibregl.Marker({ color: '#04648d' })
      .setLngLat(e.lngLat)
      .setPopup(new maplibregl.Popup({ closeButton: false }).setHTML(
        `<div style="font-size:13px;font-weight:600;color:#111">${escapeHtml(text)}</div>`
      ))
      .addTo(map);
    annotations.push({ kind: 'pin', text, lngLat: e.lngLat, marker });
  } else {
    // Text-only: a div marker styled as a typographic label
    const el = document.createElement('div');
    el.className = 'map-text-anno';
    el.textContent = text;
    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(e.lngLat)
      .addTo(map);
    annotations.push({ kind: 'text', text, lngLat: e.lngLat, marker });
  }

  toast(`${mode === 'pin' ? 'Pin' : 'Text'} annotation added`);
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
